import { useState, useMemo, useCallback, useRef } from "react";
import { message } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import {
  HEADERS_EXPORT_BEN,
  POST_ADD_DEFENDANT,
  POST_LOAD_GURANTOR,
  POST_STATUS_JUDGMENT,
  POST_UPLOAD_IMAGE_NEW,
  HEADERS_UPIMAGE,
  POST_LOAN_DB2,
} from "../../../API/apiUrls";

const useJudgmentData = ({ dataDefault, form }) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));

  // States
  const [loading, setLoading] = useState(false);
  const [dataLoadGurantor, setDataLoadGurantor] = useState(null);
  const [checkboxTab1, setCheckBoxTab1] = useState([]);
  const [checkboxTab2, setCheckBoxTab2] = useState([]);
  const [formDataTab1, setFormDataTab1] = useState(null);
  const [selectedDefendantsTab1, setSelectedDefendantsTab1] = useState([]);
  const [selectedDefendantsTab2, setSelectedDefendantsTab2] = useState([]);
  const isFirstLoadGurantor = useRef(true);

  // ========================================
  // Computed Values
  // ========================================

  const allDefendants = useMemo(() => {
    if (!dataLoadGurantor) return [];

    const people = [];

    if (dataLoadGurantor.customer) {
      people.push({
        ...dataLoadGurantor.customer,
        type: "customer",
        typeLabel: "ผู้เช่าซื้อ",
        defendantNumber: 1,
      });
    }

    if (dataLoadGurantor.guarantors?.length > 0) {
      dataLoadGurantor.guarantors.forEach((g, index) => {
        people.push({
          ...g,
          type: "guarantor",
          typeLabel: `ผู้ค้ำที้ ${g.garno}`,
          defendantNumber: index + 2,
        });
      });
    }

    return people;
  }, [dataLoadGurantor]);

  const guarantorsOnly = useMemo(() => {
    return allDefendants.filter((p) => p.type !== "customer");
  }, [allDefendants]);

  const customerCustomerId = useMemo(() => {
    return dataLoadGurantor?.customer?.CUSTOMER_ID || null;
  }, [dataLoadGurantor]);

  const nextGarno = useMemo(() => {
    if (!dataLoadGurantor?.guarantors?.length) return 1;
    const maxGarno = Math.max(
      ...dataLoadGurantor.guarantors.map((g) => g.garno || 0),
    );
    return maxGarno + 1;
  }, [dataLoadGurantor]);

  // ========================================
  // API Functions
  // ========================================

  const loadGurantor = useCallback(async () => {
    setLoading(true);

    try {
      const res = await axios.post(
        POST_LOAD_GURANTOR,
        { lawsuitId: dataDefault.lawsuitId },
        { headers: HEADERS_EXPORT_BEN },
      );

      if (res.data) {
        setDataLoadGurantor(res.data);
        if (isFirstLoadGurantor.current) {
          form.setFieldsValue({
            courtFee1: res.data.fee || 0,
            trackingFeeEnforce1: 0,
            lawyerFeeEnforce1: 0,
          });
          isFirstLoadGurantor.current = false;
        }

        return res.data;
      } else {
        message.info("ไม่มีข้อมูล");
        return null;
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loading data:", error);
        message.error(
          error.response?.data?.message || "ไม่สามารถโหลดข้อมูลได้",
        );
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [dataDefault.lawsuitId, form]);

  const postStatus = useCallback(async (payload) => {
    console.log("POST_STATUS_JUDGMENT:", payload);

    try {
      const res = await axios.post(POST_STATUS_JUDGMENT, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (res.status === 200) {
        message.success("บันทึกข้อมูลสำเร็จ");
        return res.status;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(`ไม่สามารถส่งข้อมูลได้: ${serverMsg}`);
      }
      // setTimeout(() => {
      //   window.location.reload();
      // }, 1000);
      throw error;
    }
  }, []);

  const addCustomerJudge = useCallback(
    async (values) => {
      const payload = {
        garno: nextGarno,
        RELATN: values.relation || null,
        loanId: dataDefault.loanId,
        CUSCOD: values.idCard || "",
        SNAM: values.prefix || "",
        NAME1: values.firstName || "",
        NAME2: values.lastName || "",
        BIRTHDT: values.birthDate ? dayjs(values.birthDate).format() : null,
        NATION: values.nation || null,
      };

      console.log("POST_ADD_CUSTOMER_JUDGE:", payload);
      setLoading(true);

      try {
        const res = await axios.post(POST_ADD_DEFENDANT, payload, {
          headers: HEADERS_EXPORT_BEN,
        });

        if (res.status === 200 || res.status === 201) {
          message.success("เพิ่มจำเลยสำเร็จ");
          await loadGurantor();
          return res.data;
        }
      } catch (error) {
        const serverMsg =
          error?.response?.data?.message || "ไม่สามารถเพิ่มจำเลยได้";
        message.error(serverMsg);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [nextGarno, dataDefault.loanId, loadGurantor],
  );

  const handleUpLoadIMG = useCallback(async (resultImgs, menu, data) => {
    setLoading(true);
    const newFormData = new FormData();

    try {
      newFormData.append("id", data.id);
      newFormData.append("notis", menu);

      const sortedImgs = resultImgs
        .filter((img) => img.originalIndex !== undefined)
        .sort((a, b) => {
          const typeDiff = a.type - b.type;
          if (typeDiff !== 0) return typeDiff;
          return a.originalIndex - b.originalIndex;
        });

      for (const file of sortedImgs) {
        newFormData.append("file", file.imageCompressedFile);
        newFormData.append("menuType", menu);
        newFormData.append("imageType", file.type);
      }

      const response = await axios.post(POST_UPLOAD_IMAGE_NEW, newFormData, {
        headers: HEADERS_UPIMAGE,
      });

      if (response.status === 200) {
        message.success("บันทึกรูปสำเร็จ");
        return response.status;
      }
    } catch (error) {
      message.error("บันทึกรูปไม่สำเร็จ");
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // Checkbox Handlers
  // ========================================

  // ✅ แก้ไข: Filter objects จาก allDefendants เสมอ ไม่ว่าจะมาจากไหน
  const handleCheckboxTab1Change = useCallback(
    (checkedValues, selectedObjects) => {
      // บังคับให้ผู้เช่าซื้อต้อง checked เสมอ
      if (customerCustomerId && !checkedValues.includes(customerCustomerId)) {
        checkedValues = [...checkedValues, customerCustomerId];
      }

      // ✅ แก้ไข: Filter objects จาก allDefendants โดยตรง (ไม่พึ่ง selectedObjects)
      const filteredObjects = allDefendants.filter((defendant) =>
        checkedValues.includes(defendant.CUSTOMER_ID),
      );

      console.log(" Tab1 Selected IDs:", checkedValues);
      console.log(" Tab1 Selected Objects:", filteredObjects);

      setCheckBoxTab1(checkedValues);
      setSelectedDefendantsTab1(filteredObjects); // ✅ ใช้ filteredObjects แทน
      form.setFieldsValue({ governmentOfficer1: checkedValues });
    },
    [customerCustomerId, form, allDefendants],
  );

  const customerCustomerIdAll = useMemo(() => {
    return allDefendants.map((p) => p.CUSTOMER_ID);
  }, [allDefendants]);

  // ✅ แก้ไข Tab2 เช่นกัน
  const handleCheckboxTab2Change = useCallback(
    (checkedValues, selectedObjects) => {
      // ✅ Filter objects จาก guarantorsOnly โดยตรง
      const filteredObjects = guarantorsOnly.filter((defendant) =>
        checkedValues.includes(defendant.CUSTOMER_ID),
      );

      console.log(" Tab2 Selected IDs:", checkedValues);
      console.log(" Tab2 Selected Objects:", filteredObjects);

      setCheckBoxTab2(checkedValues);
      setSelectedDefendantsTab2(filteredObjects); // ✅ ใช้ filteredObjects แทน
      form.setFieldsValue({ governmentOfficer2: checkedValues });
    },
    [form, guarantorsOnly],
  );

  const resetCheckboxes = useCallback(() => {
    setCheckBoxTab1([]);
    setCheckBoxTab2([]);
    setSelectedDefendantsTab1([]);
    setSelectedDefendantsTab2([]);
  }, []);

  // ========================================
  // Form Data Functions
  // ========================================

  const saveFormDataTab1 = useCallback(() => {
    const currentValues = form.getFieldsValue();

    const dataWithDefendants = {
      ...currentValues,
      selectedDefendants: selectedDefendantsTab1,
    };

    console.log("💾 Saving Tab1 data:", dataWithDefendants);
    setFormDataTab1(dataWithDefendants);
    return dataWithDefendants;
  }, [form, selectedDefendantsTab1]);

  const getMergedFormData = useCallback(() => {
    const currentValues = form.getFieldsValue();
    const mergedData = {
      ...formDataTab1,
      ...currentValues,
      selectedDefendantsTab1: selectedDefendantsTab1,
      selectedDefendantsTab2: selectedDefendantsTab2,
    };
    console.log("🔀 Merged data:", mergedData);
    return mergedData;
  }, [form, formDataTab1, selectedDefendantsTab1, selectedDefendantsTab2]);

  const resetFormDataTab1 = useCallback(() => {
    setFormDataTab1(null);
  }, []);

  // ========================================
  // Return
  // ========================================

  return {
    // States
    loading,
    setLoading,
    dataLoadGurantor,
    checkboxTab1,
    checkboxTab2,
    formDataTab1,
    selectedDefendantsTab1,
    selectedDefendantsTab2,

    // Computed
    allDefendants,
    guarantorsOnly,
    customerCustomerId,
    customerCustomerIdAll,
    nextGarno,
    userId,

    // Functions
    loadGurantor,
    postStatus,
    addCustomerJudge,
    handleCheckboxTab1Change,
    handleCheckboxTab2Change,
    resetCheckboxes,
    saveFormDataTab1,
    getMergedFormData,
    resetFormDataTab1,
    handleUpLoadIMG,
  };
};

export default useJudgmentData;
